// Copyright (c) 2019 Comsearch, Inc., All rights reserved

import { error } from 'react-notification-system-redux'
import { loadingToggler } from 'redux/modules/loading'
import { throwSubmissionError } from 'helpers/reduxFormFunctions'
import { setPathValidationsMessagesInDrawer } from 'redux/modules/pathValidationsDrawer'
import { UNCAUGHT_ERROR } from 'errors'

import getPathDetailsCallsignCascadeTriggerData from '../getPathDetailsCallsignCascadeTriggerData'

import {
  assignDefaultContactToSite,
  parseValidationMessages,
} from 'redux/helpers'

import {
  formatPathDetailsData,
  handleSuccessfulSave,
  updateProjectSummary,
} from './helpers'

import {
  postNewPath,
  putPathDetails,
} from 'helpers/api'

import { change } from 'redux-form'

import {
  togglePassiveRepeaterDisplay,
  updatePathDetails,
} from 'redux/modules/pathDetails'

const handlePathDetailsSubmit = (formValues, openContextModal, handleSuccessfulSubmitCallback) => (dispatch, getState) => (
  dispatch(loadingToggler(
    async () => {
      const { pathDetails } = getState()

      const projectId = pathDetails.get('projectId')
      // the form's name for redux-form
      const sourceForm = pathDetails.get('sourceForm')
      // default contact and company from the pathDetails, not project summary
      const defaultCompany = pathDetails.get('defaultCompany')
      const defaultContact = pathDetails.get('defaultContact')

      // convert the path data and make it ready for the API
      const pathData = formatPathDetailsData(formValues)
      pathData.projectId = projectId // API wants to now this id within the JSON

      const isNewPath = !pathData.pathId

      // if the band is changed after a passive repeater set is saved, the passive repeater
      // set will then be 'incomplete' because of this the user will be prompted to save
      // antenna information for the passive repeater(s) before saving the path's data.
      // check if the passive data set is incomplete
      let incompletePassives = false
      if (pathData.segments.length > 1) {
        incompletePassives = pathData.segments.reduce((a, d, i) => {
          // !i here because the first pass through will be site1
          // and at this point in the work flow we don't care
          // if site1/2 have antennas. only passive repeaters and only because
          // the UI requires passive repeaters be entirely defined on save.
          if (!a || !i) {
            return a
          }

          // every passive segment end antenna must have an assigned antennaSpecId
          // if any one does not, then prompt the user to define
          return !d.segmentEnds[0].antennas.every(ant => ant.antennaSpecId)
        }, true)
      }

      try {
        // if it is, throw an error for this situation.
        if (incompletePassives) {
          const errorMessage = 'Please assign an antenna to Passive Repeater'
          dispatch(change(sourceForm, 'validatePassiveRepeaters', true))
          dispatch(togglePassiveRepeaterDisplay())
          throw new Error(errorMessage)
        }

        // determines if any of the updated fields should cause a callsign cascade 2 to trigger.
        // we need to figure this out before we call updatePathDetails()
        // because that will clear the initial values.
        // we pass this to handleSuccessfulSave() so that it can determine if
        // it will show a successful save modal or a Callsign Cascade 2 modal.
        const callsignCascadeTriggerData = dispatch(getPathDetailsCallsignCascadeTriggerData())

        // if a site doesn't have a contact/company in this payload,
        // assign whatever the project's default is
        assignDefaultContactToSite(pathData, defaultCompany, defaultContact)

        const res = await (
          isNewPath
            ? postNewPath(pathData)
            : putPathDetails(pathData)
        )

        const {
          entity,
          message,
          status,
        } = res

        await dispatch(updatePathDetails(entity, isNewPath))

        await dispatch(updateProjectSummary(entity, isNewPath))

        await dispatch(handleSuccessfulSave(res, callsignCascadeTriggerData, openContextModal, handleSuccessfulSubmitCallback))

        // This is pretty edge case and probably isn't possible with valid db data but...
        // If the Path Save was successful, but the Callsign Cascade 1 failed,
        // the API will respond with HTTP 206, providing the updated Path Details entity
        // but also providing a message to present to the user telling them of the Callsign Cascade 1 failure.
        // This message will tell the user something to the effect of:
        // Note: Call sign cascade for (expiration date / radio service / station class) did not occur - please contact customer support.
        if (status === 'Error' && message) {
          dispatch(error({
            autoDismiss: 0,
            message,
            title: 'Error:',
          }))
        }

        // finally, call this call back so we can update the path cart store in case we need to
        handleSuccessfulSubmitCallback()
      } catch (err) {
        console.warn(err)

        // if it's a new path, then the path id is 0.
        // This way we can avoid trying to do something like
        // pathValidations.null.errors
        const pathId = isNewPath ? 0 : pathData.pathId
        const newPathValidations = {}

        if (err?.json?.errors) {
          const parsedValidationMessages = parseValidationMessages(err.json.errors)

          const validation = parsedValidationMessages[pathId]

          newPathValidations[pathId] = {
            errors: validation.messages,
            heading: validation.heading,
            warnings: [],
          }
        }

        dispatch(setPathValidationsMessagesInDrawer(
          err.message || UNCAUGHT_ERROR,
          newPathValidations
        ))

        // throws error to redux-form
        throwSubmissionError(err)
      }
    })
  )
)

export default handlePathDetailsSubmit
